import { NextSeo } from "next-seo";
import SimpleFooter from "components/SimpleFooter";
import SimpleNavbar from "components/SimpleNavbar";
import Link from "next/link";

const PrivacyPolicy = () => {
  return (
    <>
      <NextSeo title="Teamshares | Privacy Policy" />
      <div className="min-h-screen antialiased leading-normal text-gray-900 bg-white">
        <SimpleNavbar />
        <div className="max-w-6xl px-12 mx-auto text-sm lg:text-lg">
          <div className="mb-10 text-3xl font-bold pt-28 md:text-4xl">
            Teamshares Inc. Privacy Policy
          </div>
          <div className="mb-2 font-bold lg:mb-4">
            Last Updated: <span className="font-normal"> 08/14/2023</span>
          </div>
          Teamshares Inc. and its affiliates, including its subsidiaries (
          <span className="font-bold">&ldquo;Teamshares</span>,
          <span className="font-bold">&rdquo; &ldquo;we</span>,
          <span className="font-bold">&rdquo; &ldquo;our</span>,
          <span className="font-bold">&rdquo; </span>and/or
          <span className="font-bold">&nbsp;&ldquo;us&rdquo;</span>), value the
          privacy of individuals who use our platform, application, websites
          (including https://www.teamshares.com and https://os.teamshares.com),
          and related services (collectively, our
          <span className="font-bold">&nbsp;&ldquo;Services&rdquo;</span>). This
          privacy policy (the
          <span className="font-bold">&nbsp;&ldquo;Privacy Policy&rdquo;</span>)
          explains how we collect, use, and disclose personal information from
          users of our Services. By using our Services, you agree to the
          collection, use, and disclosure of your personal information as
          described in this Privacy Policy. Beyond this Privacy Policy, your use
          of our Services is also subject to our{" "}
          <Link href="/terms-and-conditions" target="_blank" rel="noopener">
            Terms of Service
          </Link>
          . If you are a California resident, please also review our{" "}
          <Link
            href="/california-resident-privacy-notice"
            target="_blank"
            rel="noopener"
          >
            California Resident Privacy Notice
          </Link>{" "}
          for more information about the types of personal information we
          collect and disclose, as well as how to exercise your rights under
          California law.
          <div className="mt-2 font-bold underline lg:mt-4">
            Personal Information We Collect
          </div>
          <div className="mt-2 lg:mt-4"></div>
          We may collect a variety of personal information from or about you or
          your devices from various sources, as described below.
          <div className="mt-2 mb-2 font-bold lg:mt-4 lg:mb-4">
            A. Personal Information You Provide to Us
          </div>
          <span className="mr-1 font-bold">Registration.</span>
          When you register for an account to use our Services, we may ask you
          for your personal information, including your name, address, employer
          name, email address, and phone number.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Use of our Services.</span>
          When you use our Services, we collect the personal information you
          provide to us. For example, when you use our Services to manage your
          business, we may collect your business contact information and
          information regarding your employees, including their employee email
          address, salary and equity grant information, and any other
          information you provide to us. Additionally, when you use our Services
          in connection with our Financial Partners, we may collect your name,
          birth date, government-issued identification number, and in certain
          instances, payment card and bank information.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Communications.</span>
          If you contact us directly, we may receive additional personal
          information about you. For example, when you contact us for support,
          we will receive your name, email address, the contents of your
          message, and your phone number if you choose to provide it. If you
          subscribe to our marketing communications, we will receive your name
          and email address.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Careers.</span>
          If you decide that you wish to apply for a job with us, you may submit
          your contact information and your resume online. We will collect the
          information you choose to provide on your resume, such as your
          education and employment experience. You may also apply through
          LinkedIn. If you do so, we will collect the information you make
          available to us on LinkedIn.
          <div className="mt-2 font-bold lg:mt-4">
            B. Personal Information We Collect When You Use Our Services
          </div>
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Location Information.</span>
          When you use our Services, we may infer your general location
          information. For example, your IP address may indicate your general
          geographic region.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Device Information.</span>
          We receive information about the device and software you use to access
          our Services, including internet protocol (IP) address, web browser
          type, and operating system version.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Usage Information. </span>To help us
          understand how you use our Services and to help us improve them, we
          automatically receive information about your interactions with them,
          such as the length of time you spend on a website page, objects such
          as hyperlinks you click on, and the dates and times of your visits.
          When we send you emails, we may track whether you open them to learn
          how to deliver a better customer experience.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Analytics Partners. </span>We use
          analytics services such as Google Analytics, Segment to collect and
          process certain analytics data. These services may also collect
          information about your use of other websites, apps, and online
          resources. You can learn about Google&rsquo;s practices by going
          to&nbsp;
          <Link
            href="https://www.google.com/policies/privacy/partners/"
            target="_blank"
            rel="noopener"
          >
            https://www.google.com/policies/privacy/partners/
          </Link>
          .<div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">
            Information from Cookies and Similar Technologies.{" "}
          </span>
          We and our third-party partners may collect information using cookies,
          pixel tags, or similar technologies. Our third-party partners, such as
          analytics partners, may use these technologies to collect information
          about your online activities over time and across different services.
          Cookies are small text files containing a string of alphanumeric
          characters. We may use both session cookies and persistent cookies. A
          session cookie disappears after you close your browser. A persistent
          cookie remains after you close your browser and may be used by your
          browser on subsequent visits to our Services. Please review your web
          browser&rsquo;s &ldquo;Help&rdquo; file to learn how you may modify
          your cookie settings. Please note that if you delete or choose not to
          accept cookies from the Services, you may not be able to utilize the
          features of the Services to their fullest potential.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">
            C. Personal Information We Receive from Third Parties
          </span>
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Partners.</span>
          We may receive personal information about you from third parties such
          as data or marketing partners and combine it with other personal
          information we have about you.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Employers.</span>
          If your employer uses our Services to manage their business, we
          collect the personal information about you provided to us by your
          employer, including your name, profession, salary and equity grant
          information, tenure information, and email address.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Financial Partners.</span>
          Our &ldquo;Financial Partners&rdquo; are the financial institutions
          that we partner with to offer certain Services. We may share your
          name, birth date, government-issued identification number, and in
          certain instances, payment card and bank information with certain
          Financial Partners to provide our Services and offer certain Services
          in partnership with our Financial Partners.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">
            How We Use the Personal Information We Collect
          </span>
          <div className="mt-2 lg:mt-4"></div>
          We use the personal information we collect:
          <div className="mt-2 lg:mt-4"></div>
          <ul className="list-disc">
            <li>
              To provide, maintain, debug, improve, and enhance our products and
              the Services;
            </li>
            <li>
              To understand and analyze how you use our Services, and develop
              new products, services, features, and functionality;
            </li>
            <li>
              To provide and deliver products and services to employers with
              respect to their employees ;
            </li>
            <li>To display your equity grant information;</li>
            <li>To understand and analyze usage patterns;</li>
            <li>
              To communicate with you, provide you with updates and other
              information relating to our Services, provide information that you
              request, respond to comments and questions, and otherwise provide
              customer support;
            </li>
            <li>To send you push notifications and/or text messages;</li>
            <li>
              To generate de-identified and aggregated data for any lawful
              purpose;
            </li>
            <li>
              For marketing purposes, such as developing and providing
              promotional and advertising materials that may be useful,
              relevant, valuable, or otherwise of interest to you;
            </li>
            <li>
              To find and prevent fraud, and respond to trust and safety issues
              that may arise;
            </li>
            <li>
              For compliance purposes, including enforcing our Terms of Service
              or other legal rights, or as may be required by applicable laws
              and regulations or requested by any judicial process or
              governmental agency; and
            </li>
            <li>
              For other purposes for which we provide specific notice at the
              time the personal information is collected.
            </li>
          </ul>
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">
            How We Disclose the Personal Information We Collect
          </span>
          <div className="mt-2 lg:mt-4"></div>
          We do not disclose personal information we collect from or about you
          except as described below or otherwise disclosed to you at the time of
          the collection.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Affiliates.</span>
          We may disclose personal information we receive with our affiliates
          for any of the purposes described in this Privacy Policy.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Vendors and Service Providers.</span>
          We may disclose any personal information we receive with vendors and
          service providers retained in connection with the provision of our
          Services.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Merchants.</span>
          We may disclose personal information we receive with our merchants if
          there is a payment dispute between you and one of our merchants.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Authorized Users.</span>
          We may disclose personal information with your employer&rsquo;s equity
          administrators, authorized users, and other designated representatives
          to access information that we hold about the equity information of
          their employees.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Marketing.</span>
          We do not rent, sell, or share personal information about you with
          nonaffiliated companies for their direct marketing purposes unless we
          have your permission.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Analytics Partners.</span>
          We may use analytics services such as Google Analytics, &nbsp; &nbsp;
          &nbsp;Segment and Hotjar to collect and process certain analytics
          data. These services may also collect information about your use of
          other websites, apps, and online resources. You can learn about
          Google&rsquo;s practices by going to&nbsp;
          <Link
            href="https://www.google.com/policies/privacy/partners/"
            target="_blank"
            rel="noopener"
          >
            https://www.google.com/policies/privacy/partners/
          </Link>
          .<div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">
            As Required by Law and Similar Disclosures.
          </span>
          We may access, preserve, and disclose your personal information if we
          believe doing so is required or appropriate to: (a) comply with law
          enforcement requests and legal process, such as a court order or
          subpoena; (b) respond to your requests; or (c) protect your, our, or
          others&rsquo; rights, property, or safety.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">
            Merger, Sale, or Other Asset Transfers.
          </span>
          We may transfer your personal information to service providers,
          advisors, potential transactional partners, or other third parties in
          connection with the consideration, negotiation, or completion of a
          corporate transaction in which we are acquired by or merged with
          another company or we sell, liquidate, or transfer all or a portion of
          our assets.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Consent.</span>
          We may also disclose your personal information with your permission.
          When we process data based on your consent, you have the right to
          withdraw your consent at any time without affecting the lawfulness of
          processing based on such consent before the consent is withdrawn.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Your Choices</span>
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Marketing Communications.</span>
          You can unsubscribe from our promotional emails via the link provided
          in the emails. Even if you opt-out of receiving promotional messages
          from us, you will continue to receive administrative messages from us.
          <div className="mt-2 lg:mt-4"></div>
          <span className="mr-1 font-bold">Do Not Track.</span>
          There is no accepted standard on how to respond to Do Not Track
          signals, and we do not respond to such signals.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Data Retention</span>
          <div className="mt-2 lg:mt-4"></div>
          We take measures to delete your information or keep it in a form that
          does not permit identifying you when this information is no longer
          necessary for the purposes for which we process it, unless we are
          required by law to keep this information for a longer period.&nbsp;
          When determining the retention period, we consider various criteria,
          such as the type of products and services requested by or provided to
          you, the nature and length of our relationship with you, the impact on
          the Services we provide to you if we delete some information from or
          about you, mandatory retention periods provided by law, and any
          relevant statute of limitations.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Third Parties</span>
          <div className="mt-2 lg:mt-4"></div>
          Our Services may contain links to other websites, products, or
          services that we do not own or operate. We are not responsible for the
          privacy and advertising practices of these third parties. Please be
          aware that this Privacy Policy does not apply to your activities on
          these third-party services or any personal information you disclose to
          these third parties. We encourage you to read their privacy policies
          before providing any personal information to them.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Security</span>
          <div className="mt-2 lg:mt-4"></div>
          We make reasonable efforts to protect your personal information by
          using physical and electronic safeguards designed to improve the
          security of the personal information we maintain. However, because no
          electronic transmission or storage of personal information can be
          entirely secure, we can make no guarantees as to the security or
          privacy of your personal information.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">International Visitors</span>
          <div className="mt-2 lg:mt-4"></div>
          Our Services are hosted in the United States and intended for visitors
          located within the United States. If you choose to use our Services
          from the European Union or other regions of the world with laws
          governing data collection and use that may differ from U.S. law, then
          please note that you are transferring your personal information
          outside of those regions to the U.S. for storage and processing. We
          may also transfer your data from the U.S. to other countries or
          regions in connection with storage and processing of data, fulfilling
          your requests, and operating the Services. By providing any
          information, including personal information, on or to our Services,
          you consent to such transfer, storage, and processing.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Children&rsquo;s Privacy</span>
          <div className="mt-2 lg:mt-4"></div>
          We do not knowingly collect, maintain, or use personal information
          from children under 13 years of age, and no parts of our Services are
          directed to children. If you learn that a child has provided us with
          personal information in violation of this Privacy Policy, then you may
          alert us at{" "}
          <Link
            href="mailto:support@teamshares.com"
            target="_blank"
            rel="noopener"
          >
            support@teamshares.com
          </Link>
          .<div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Pose a Question</span>
          <div className="mt-2 lg:mt-4"></div>
          If you have questions about your privacy on the Services or this
          Privacy Policy, please contact us at{" "}
          <Link
            href="mailto:support@teamshares.com"
            target="_blank"
            rel="noopener"
          >
            support@teamshares.com
          </Link>
          .<div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">
            Changes to this Privacy Policy
          </span>
          <div className="mt-2 lg:mt-4"></div>
          We will post any adjustments to the Privacy Policy on this page, and
          the revised version will be effective when it is posted. If we
          materially change the ways in which we use or disclose personal
          information previously collected from you through our Services, we
          will attempt to notify you through our Services, by email, or other
          means.
          <div className="mt-2 lg:mt-4"></div>
          <span className="font-bold underline">Contact Information.</span>
          <div className="mt-2 lg:mt-4"></div>
          Teamshares is responsible for processing your information. If you have
          any questions, comments, or concerns about our processing activities,
          please email us at{" "}
          <Link
            href="mailto:support@teamshares.com"
            target="_blank"
            rel="noopener"
          >
            support@teamshares.com
          </Link>{" "}
          or write to us at:
          <div className="mt-2 lg:mt-4"></div>
          Teamshares Inc.
          <br />
          214 Sullivan Street, Suite 6B
          <br />
          NY, NY 10012
        </div>
        <SimpleFooter />
      </div>
    </>
  );
};

export default PrivacyPolicy;
